import axios from 'axios';
import authService from './auth';

// Base API URL
import { API_BASE_URL } from '../utils/constants';

// Authenticated endpoints
const API_ENDPOINTS = {
  LIST: `${API_BASE_URL}/api/field-categories`,
  CREATE: `${API_BASE_URL}/api/field-categories`,
  UPDATE: (code) => `${API_BASE_URL}/api/field-categories/${code}`,
  DELETE: (code) => `${API_BASE_URL}/api/field-categories/${code}`,
  ACTIVATE: (code) => `${API_BASE_URL}/api/field-categories/${code}/activate`
};

// Public endpoints (as fallback)
const PUBLIC_API_ENDPOINTS = {
  LIST: `${API_BASE_URL}/v1/api/public/field-categories`
};

/**
 * Service for field category management
 */
const fieldCategoryService = {
  /**
   * Get all field categories
   * @param {boolean} usePublicEndpoint - Whether to use the public endpoint
   * @returns {Promise} - Promise that resolves to the list of categories
   */
  getCategories: async (usePublicEndpoint = false) => {
    try {
      // Try authenticated endpoint first, unless specifically asked to use public
      if (!usePublicEndpoint) {
        const token = authService.getToken();
        const response = await axios.get(API_ENDPOINTS.LIST, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        return response.data;
      } else {
        // Fallback to public endpoint
        const response = await axios.get(PUBLIC_API_ENDPOINTS.LIST);
        return response.data;
      }
    } catch (error) {
      // If authenticated endpoint fails, try public endpoint as fallback
      if (!usePublicEndpoint && error.response?.status === 401) {
        console.warn('Authentication failed, falling back to public endpoint');
        return fieldCategoryService.getCategories(true);
      }
      throw error;
    }
  },

  /**
   * Create a new field category
   * @param {Object} category - Category data
   * @returns {Promise} - Promise that resolves to the created category
   */
  createCategory: async (category) => {
    const token = authService.getToken();
    const response = await axios.post(API_ENDPOINTS.CREATE, category, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },

  /**
   * Update an existing field category
   * @param {string} code - Category code
   * @param {Object} category - Updated category data
   * @returns {Promise} - Promise that resolves to the updated category
   */
  updateCategory: async (code, category) => {
    const token = authService.getToken();
    const response = await axios.put(API_ENDPOINTS.UPDATE(code), category, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },

  /**
   * Delete a field category
   * @param {string} code - Category code
   * @returns {Promise} - Promise that resolves when category is deleted
   */
  deleteCategory: async (code) => {
    const token = authService.getToken();
    const response = await axios.delete(API_ENDPOINTS.DELETE(code), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  },

  /**
   * Activate a field category
   * @param {string} code - Category code
   * @returns {Promise} - Promise that resolves when category is activated
   */
  activateCategory: async (code) => {
    const token = authService.getToken();
    const response = await axios.put(API_ENDPOINTS.ACTIVATE(code), {}, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  }
};

export default fieldCategoryService;
